import algoliasearch from 'algoliasearch/lite';
import instantsearch, { GeoLoc, UiState } from 'instantsearch.js';
import {
  connectMenu,
  connectClearRefinements,
  connectCurrentRefinements,
  connectSearchBox,
  connectAutocomplete,
  connectGeoSearch,
  connectSortBy,
} from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  hits,
  pagination,
  refinementList,
  configure,
  clearRefinements,
  toggleRefinement,
  queryRuleCustomData,
  stats,
} from 'instantsearch.js/es/widgets';
import DOMPurify from 'isomorphic-dompurify';

import { autocompleteSearch } from '@/src/utilities/search/searchRenderFunctions';
import {
  cleanQueryString,
  deg2rad,
  getRoundedRadiusFromUrl,
  getZipcodeFromUrl,
  insertUrlZipcode,
  newYorkCities,
  removeUrlZipcode,
  shortenZipcode,
  toggleDistanceAccordion,
  showDivider,
} from '@/src/utilities/search/utils';

export async function loadProviderSearch(): Promise<void> {
  const config = window['provider-search'];

  if (!config) {
    return;
  }

  const {
    applicationId,
    searchKey,
    searchContext,
    searchIndexName,
    placeholderImageURL,
    defaultPhoneNumber,
    openSchedulerAPIKey,
    openSchedulerDataURL,
  } = config;

  const { searchTerm: query } = searchContext;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: `${searchIndexName.default}`,
    searchClient,
    insights: true,
    routing: {
      router: history({
        createURL({ qsModule, routeState, location }) {
          const urlParts = location.href.match(/^(.*?)\/search/);
          const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

          type QueryParams = {
            term?: string;
            zipcode?: string;
            sortBy?: string;
            isAcceptingNewPatients?: boolean;
            relatedConditions?: string[];
            languages?: string[];
            distance?: string;
            relatedDepartments?: string[];
            hasOnlineScheduling?: boolean;
            gender?: string;
            page?: number;
          };

          const queryParameters = {} as QueryParams;
          const pageNumber = routeState['page'] as number;

          // set the query
          if (routeState['term']) {
            queryParameters.term = routeState['term'] as string;
          }

          // set the zipcode / city
          if (routeState['zipcode']) {
            queryParameters.zipcode = routeState['zipcode'] as string;
          }

          // set the sorting method
          if (!routeState['sortBy']) {
            queryParameters.sortBy = 'Availability';
          } else if (routeState['sortBy'] === `${searchIndexName.distance}`) {
            queryParameters.sortBy = 'Distance';
          } else {
            queryParameters.sortBy = 'Alphabetical';
          }

          // accepting new patients
          if (routeState['isAcceptingNewPatients']) {
            queryParameters.isAcceptingNewPatients = true;
          }

          // RelatedConditions
          if (routeState['relatedConditions']) {
            queryParameters.relatedConditions = routeState[
              'relatedConditions'
            ] as string[];
          }

          // set the primary care type facets
          if (routeState['languages']) {
            queryParameters.languages = routeState['languages'] as string[];
          }

          // set the distance
          if (routeState['distance']) {
            queryParameters.distance = routeState['distance'] as string;
          }

          // RelatedDepartments
          if (routeState['relatedDepartments']) {
            queryParameters.relatedDepartments = routeState[
              'relatedDepartments'
            ] as string[];
          }

          // online scheduling
          if (routeState['isAllowOpenScheduling']) {
            queryParameters.hasOnlineScheduling = true;
          }

          // gender
          if (
            routeState['gender'] &&
            typeof routeState['gender'] === 'string'
          ) {
            queryParameters.gender =
              routeState['gender'] === 'M'
                ? 'Male'
                : routeState['gender'] === 'F'
                ? 'Female'
                : 'Non-Binary';
          }

          // set the page number
          if (pageNumber !== 1) {
            queryParameters.page = pageNumber;
          }

          const queryString = qsModule.stringify(queryParameters, {
            addQueryPrefix: true,
            arrayFormat: 'repeat',
          });

          return `${baseUrl}search${cleanQueryString(queryString)}`;
        },

        parseURL({ qsModule, location }): UiState {
          const {
            query = '',
            zipcode = '',
            sortBy = '',
            isAcceptingNewPatients = false,
            relatedConditions = [],
            languages = [],
            distance = '',
            relatedDepartments = [],
            hasOnlineScheduling = false,
            gender = '',
            page,
          } = qsModule.parse(location.search.slice(1));
          // `qs` does not return an array when there's a single value.

          const indexName: string =
            sortBy === 'Availability'
              ? `${searchIndexName.nextAvailableAppointment}`
              : sortBy === 'Distance'
              ? `${searchIndexName.distance}`
              : sortBy === 'Alphabetical'
              ? `${searchIndexName.alphabetical}`
              : `${searchIndexName.default}`;

          const genderValue: string =
            gender === 'Male'
              ? 'M'
              : gender === 'Female'
              ? 'F'
              : gender === 'Non-Binary'
              ? 'NB'
              : '';

          const selectedLanguages = Array.isArray(languages)
            ? languages
            : [languages].filter(Boolean);

          const selectedRelatedConditions = Array.isArray(relatedConditions)
            ? relatedConditions
            : [relatedConditions].filter(Boolean);

          const selectedRelatedDepartments = Array.isArray(relatedDepartments)
            ? relatedDepartments
            : [relatedDepartments].filter(Boolean);

          return {
            term: decodeURIComponent(query as string),
            zipcode: decodeURIComponent(zipcode as string),
            sortBy: indexName,
            isAcceptingNewPatients: isAcceptingNewPatients,
            languages: selectedLanguages,
            relatedConditions: selectedRelatedConditions,
            relatedDepartments: selectedRelatedDepartments,
            distance: distance as string,
            isAllowOpenScheduling: hasOnlineScheduling,
            gender: genderValue,
            page,
          };
        },
      }),

      stateMapping: {
        stateToRoute(uiState: UiState): RouteState {
          const indexUiState = uiState[searchIndexName.default];

          const geocodeInput = document.getElementById(
            'geocodeInput'
          ) as HTMLInputElement;

          const zipcode: string | null = geocodeInput.value ?? null;

          const distanceParam: number | 'all' | undefined = roundedRadius;

          return {
            term: indexUiState?.query,
            zipcode: zipcode, // this should be a zipcode, not a lat/lng
            distance: distanceParam,
            sortBy: indexUiState?.sortBy,
            isAcceptingNewPatients:
              indexUiState?.toggle &&
              indexUiState?.toggle['IsAcceptingNewPatients'],
            languages:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['Languages'],
            relatedConditions:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['RelatedConditions'],
            relatedDepartments:
              indexUiState?.refinementList &&
              indexUiState?.refinementList['RelatedDepartments'],
            isAllowOpenScheduling:
              indexUiState?.toggle &&
              indexUiState?.toggle['IsAllowOpenScheduling'],
            gender: indexUiState?.menu && indexUiState?.menu['Gender'],
            page: indexUiState?.page,
          };
        },
        routeToState(routeState: UiState): UiState {
          const selectedLanguages = Array.isArray(routeState['languages'])
            ? routeState['languages']
            : [routeState['languages']];

          const selectedConditions = Array.isArray(
            routeState['relatedConditions']
          )
            ? routeState['relatedConditions']
            : [routeState['relatedConditions']];

          const selectedDepartments = Array.isArray(
            routeState['relatedDepartments']
          )
            ? routeState['relatedDepartments']
            : [routeState['relatedDepartments']];

          // sanitized versions
          const cleanLanguages = selectedLanguages.filter((language) => {
            const isPlainText =
              typeof language === 'string' && !/<[^>]*>/.test(language);
            const sanitized = DOMPurify.sanitize(language);

            // if sanitized value is plain text, return it
            return sanitized && isPlainText;
          });

          const cleanConditions = selectedConditions.filter((condition) => {
            const isPlainText =
              typeof condition === 'string' && !/<[^>]*>/.test(condition);
            const sanitized = DOMPurify.sanitize(condition);

            // if sanitized value is plain text, return it
            return sanitized && isPlainText;
          });

          const cleanDepartments = selectedDepartments.filter((department) => {
            const isPlainText =
              typeof department === 'string' && !/<[^>]*>/.test(department);
            const sanitized = DOMPurify.sanitize(department);

            // if sanitized value is plain text, return it
            return sanitized && isPlainText;
          });

          return {
            [searchIndexName.default]: {
              query: routeState['term'],
              sortBy: routeState['sortBy'],
              toggle: {
                IsAcceptingNewPatients: routeState['isAcceptingNewPatients'],
                IsAllowOpenScheduling: routeState['isAllowOpenScheduling'],
              },
              refinementList: {
                Languages: cleanLanguages,
                RelatedConditions: cleanConditions,
                RelatedDepartments: cleanDepartments,
              },
              menu: {
                Gender: routeState['gender'],
              },
              page: routeState['page'],
            },
          };
        },
      },
    },
    searchFunction(helper) {
      const select = document.querySelector(
        '.ais-SortBy-select'
      ) as HTMLSelectElement;

      sortingMethod = select?.value.endsWith('next_available')
        ? 'AVAILABILITY'
        : select?.value.endsWith('distance')
        ? 'DISTANCE'
        : select?.value.endsWith('alphabetical')
        ? 'LAST NAME'
        : 'ERROR';

      helper.search();
    },
  });

  let userLocation: GeoLoc | null = null;
  let roundedRadius: number | 'all' | undefined = getRoundedRadiusFromUrl();

  let sortingMethod: null | undefined | string = null;

  type RouteState = {
    term: string | undefined;
    zipcode: string | undefined;
    distance: number | 'all' | undefined;
    sortBy: string | undefined;
    isAcceptingNewPatients: boolean | undefined;
    languages: string[] | undefined;
    relatedConditions: string[] | undefined;
    relatedDepartments: string[] | undefined;
    isAllowOpenScheduling: boolean | undefined;
    gender: string | undefined;
    page: number | undefined;
  };

  const geocoder = new google.maps.Geocoder();

  window.addEventListener('load', () => {
    const geocodeInput = document.getElementById(
      'geocodeInput'
    ) as HTMLInputElement;
    geocodeInput.value = getZipcodeFromUrl();

    const zipcode = getZipcodeFromUrl();
    if (zipcode.length > 0) {
      geocode(zipcode);
    }
    toggleDistanceAccordion();
    closeSchedulerListener();
    showDivider();
  });

  const virtualSearchBox = connectSearchBox(() => null);

  // utils:

  function openScheduler(eRecordNumber: string) {
    const overlay = document.querySelector('.bg-overlay');
    const scheduleModal = document.querySelector('.schedule-modal');
    const iFrameLoader = document.getElementById('iFrameLoader');

    scheduleModal?.classList.remove('hidden');
    if (iFrameLoader != null) {
      iFrameLoader.innerHTML = `<iframe style="min-height: ${scheduleModal?.clientHeight}px" class="overflow-hidden w-full h-full" id="MyChartIframe0" src="${openSchedulerDataURL}/openscheduling/embedded?apikey=${openSchedulerAPIKey}&widgetid=MyChartIframe0&vt=&dept=&id=${eRecordNumber}&numslotsperday=2"></iframe>`;
    }

    overlay?.setAttribute('style', 'display: block');
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.position = 'fixed';
  }

  function closeSchedulerListener() {
    const scheduleModal = document.querySelector('.schedule-modal');
    const iFrameLoader = document.getElementById('iFrameLoader');
    const overlay = document.querySelector('.bg-overlay');
    const closeBtn = document.querySelector(
      '.close-modal-btn'
    ) as HTMLButtonElement;

    closeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      window.scrollTo({
        top: parseInt(scrollY || '0') * -1,
        left: 0,
        behavior: 'instant',
      });
      scheduleModal?.classList.add('hidden');
      overlay?.setAttribute('style', 'display: none');

      if (iFrameLoader != null) {
        iFrameLoader.innerHTML = ``;
      }
    });
  }

  function imageMouseEnter(eRecordNumber: string) {
    const profileLink = document.querySelector(`#link-${eRecordNumber}`);
    profileLink?.classList.add('underline', 'text-pacific-blue');
    profileLink?.classList.remove('no-underline');
  }

  function imageMouseLeave(eRecordNumber: string) {
    const profileLink = document.querySelector(`#link-${eRecordNumber}`);
    profileLink?.classList.remove('underline', 'text-pacific-blue');
    profileLink?.classList.add('no-underline');
  }

  // Function to geocode an address
  function geocode(userAddress: string) {
    const modifiedUserAddress = newYorkCities.includes(
      userAddress.toLowerCase()
    )
      ? `${userAddress}, NY`
      : shortenZipcode(userAddress);
    geocoder
      .geocode({
        address: modifiedUserAddress,
      })
      .then((r) => {
        if (r.results[0]?.geometry.location) {
          const lat = r.results[0]?.geometry.location.lat();
          const lng = r.results[0]?.geometry.location.lng();

          updateLatLng(lat, lng);
          refreshHits();

          //  programmatically update the index
          search.helper?.setIndex(`${searchIndexName.distance}`).search();

          //  programmatically update the sortBy widget's select element
          const select = document.querySelector(
            '.ais-SortBy-select'
          ) as HTMLSelectElement;
          select.value = `${searchIndexName.distance}`;
          // refresh the search to update the sortingMethod in the stats widget
          search.refresh();

          const sortedByInStatsWidget = document
            .querySelector('[data-app-search-stats]')
            ?.querySelector('p')?.lastElementChild?.innerHTML;

          if (sortingMethod !== sortedByInStatsWidget) {
            refreshStats();
          }
        } else {
          console.log('no data');
        }
      })
      .catch((e) => {
        console.log('error message', e.response.data.error_message);
      });
  }

  // function to turn GeoLoc into zipcode string and insert it into geocode input
  function reverseGeocode(userLocation: GeoLoc) {
    geocoder
      .geocode({
        location: userLocation,
      })
      .then((response) => {
        const result = response.results[0];
        const addressComponentsArray = result?.address_components;

        const zipComponent = addressComponentsArray?.find((component) =>
          component.types.includes('postal_code')
        );
        const zipcode = zipComponent?.short_name as string;

        if (zipcode) {
          const geocodeInput = document.querySelector(
            '#geocodeInput'
          ) as HTMLInputElement;

          geocodeInput.value = zipcode;
          geocodeInput.focus();
        } else {
          window.alert('No results found');
        }
      })
      .catch((e) => window.alert('Geocoder failed due to: ' + e));
  }

  function refreshHits() {
    search.removeWidgets([
      hits({
        container: '[data-app-search-hits]',
      }),
    ]);
    search.addWidgets([
      configure({
        aroundLatLng: `${userLocation?.lat},${userLocation?.lng}`,
        aroundRadius: roundedRadius,
      }),
    ]);
  }

  function refreshStats() {
    search.removeWidgets([
      stats({
        container: '[data-app-search-stats]',
      }),
    ]);
    search.addWidgets([
      stats({
        container: '[data-app-search-stats]',
        cssClasses: {
          text: 'stats-text',
        },
        templates: {
          text(data, { html }) {
            let count = '';

            const sortByContainer =
              document.querySelector('.sort-by-container');

            if (data.hasNoResults) {
              sortByContainer?.classList.add('hidden');
            } else {
              sortByContainer?.classList.remove('hidden');
            }

            if (search.status === 'error') {
              return html`
                <div>
                  <p class="text-body-regular">Search is offline</p>
                  <p>
                    Sorry, it appears our search tool is currently offline or
                    being worked on. The issue should be resolved soon.
                  </p>
                </div>
              `;
            } else if (data.hasManyResults) {
              count += `${data.nbHits} Providers`;
            } else if (data.hasOneResult) {
              count += `1 Provider`;
            } else {
              count += `Sorry, we couldn't find any results`;
              return html`
                <p class="text-body-regular">
                  <strong>${count} for: "${data.query}"</strong>
                </p>
                <p class="text-body-lg mt-10">
                  Try rephrasing your search, or try browsing our
                  <span> </span>
                  <a href="/conditions-and-treatments">condition categories</a>
                  <span> </span>
                  or
                  <span> </span>
                  <a href="/people">primary care types</a>
                  <span> </span>
                  to find what you need.
                </p>
              `;
            }

            if (data.query) {
              return html` <p>
                <strong>${count}</strong> for
                <strong> "${data.query}"</strong> sorted by
                <strong> ${sortingMethod}</strong>
              </p>`;
            } else {
              return html` <p>
                <strong>${count}</strong> sorted by
                <strong> ${sortingMethod}</strong>
              </p>`;
            }
          },
        },
      }),
    ]);
  }

  function updateRadius(selectedRadiusInMiles: string) {
    const selectedRadiusInMeters =
      parseInt(selectedRadiusInMiles, 10) * 1609.34; // Convert miles to meters

    roundedRadius =
      selectedRadiusInMiles === 'all'
        ? 'all'
        : Math.round(selectedRadiusInMeters);

    search.addWidgets([
      configure({
        aroundLatLng: `${userLocation?.lat},${userLocation?.lng}`,
        aroundRadius: roundedRadius,
      }),
    ]);
  }

  function updateLatLng(lat: number, lng: number) {
    userLocation = { lat, lng };
  }

  function getUserLatLng() {
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    function success(pos: { coords: any }) {
      const crd = pos.coords;

      userLocation = {
        lat: crd.latitude,
        lng: crd.longitude,
      };
      reverseGeocode(userLocation);
    }

    function error(err: { code: number; message: string }) {
      console.warn(`ERROR(${err.code}): ${err.message}`);
    }

    navigator.geolocation.getCurrentPosition(success, error, options);
  }

  function getDistance(providerLat: number, providerLng: number) {
    const R = 3958.8; // Radius of the earth in miles

    const userLat = userLocation?.lat as number;
    const userLng = userLocation?.lng as number;

    const providerLatDeg = providerLat || 0;
    const providerLngDeg = providerLng || 0;

    const dLat = deg2rad(userLat - providerLatDeg);
    const dLng = deg2rad(userLng - providerLngDeg);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(userLat)) *
        Math.cos(deg2rad(providerLat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in miles

    return d;
  }

  const renderAutocomplete = (
    renderOptions: {
      indices: object[];
      currentRefinement: string;
      refine: any; // should be a function
      widgetParams: any; // should be an object
    },
    isFirstRender: boolean
  ): void => {
    const { refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      const aaForm = document.createElement('form'); // aa-Form

      const aaPanel = document.createElement('div'); // dropdown panel

      const suggestionsList = document.createElement('div'); // contains 1 li per hit/suggestion
      const resultsList = document.createElement('div'); // contains 1 li per hit/suggestion
      suggestionsList.classList.add('suggestions-list');
      resultsList.classList.add('results-list');
      aaPanel.appendChild(suggestionsList);
      aaPanel.appendChild(resultsList);

      const aaInputWrapperPrefix = document.createElement('div'); // aa-InputWrapperPrefix
      const aaLabel = document.createElement('label'); // aa-Label

      const aaExtendedSearchbox = document.createElement('div'); // aa-ExtendedSearchbox
      const aaClearButton = document.createElement('button'); // aa-ClearButton
      aaClearButton.setAttribute('aria-label', 'Clear search form');

      const aaSubmitButton = document.createElement(
        'button'
      ) as HTMLButtonElement; // aa-SubmitButton

      const aaLoadingIndicator = document.createElement('div'); // aa-LoadingIndicator
      const aaLoadingIcon = document.createElement('svg'); // aa-LoadingIcon

      const aaInputWrapper = document.createElement('div'); // aa-InputWrapper
      const aaInput = document.createElement('input'); // aa-Input

      const aaExtendedGeobox = document.createElement('div');
      const geoClearButton = document.createElement('button');
      geoClearButton.setAttribute('aria-label', 'Clear city or zip code input');

      const geocodeLabel = document.createElement('label'); // geocode label
      const geocodeInput = document.createElement('input'); // geocode input
      const currentLocationContainer = document.createElement('div');
      const iconContainer = document.createElement('div');
      const currentLocationButton = document.createElement('button'); // current location button

      const aaInputWrapperSuffix = document.createElement('div'); // aa-InputWrapperSuffix

      aaInput.value = query;

      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      const zipcode = searchParams.get('zipcode');

      geocodeInput.value = zipcode ?? '';

      autocompleteSearch(query);

      aaInput.addEventListener('focus', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        }

        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('input', (event) => {
        const eventTarget = event.target as HTMLInputElement;
        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        } else {
          aaPanel.classList.add('hidden');
        }

        if (aaInput.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        } else {
          aaClearButton.classList.add('hidden');
        }

        autocompleteSearch(eventTarget.value);
      });

      let panelIsFocused = false;
      aaPanel.addEventListener('mousedown', (event) => {
        event.preventDefault();

        panelIsFocused = true;
      });

      aaClearButton.addEventListener('mousedown', (event) => {
        event.preventDefault();
        aaPanel.classList.add('hidden');
      });

      aaInput.addEventListener('blur', () => {
        searchContext.searchTerm = aaInput.value;
        autocompleteSearch(aaInput.value);

        if (!panelIsFocused) {
          aaPanel.classList.add('hidden');
        }
      });

      // add hover state on mouse enter
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseenter',
        (event) => {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            target.classList.add('bg-stone-100', 'hover:underline');
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // remove background color on mouse leave UNLESS it's the currently focused element
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseleave',
        (event) => {
          const target = event.target as HTMLElement;
          const currentFocus = document.querySelector('#selected-query');

          if (target.tagName === 'A') {
            if (target !== currentFocus) {
              target.classList.remove('bg-stone-100');
            }
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // arrow key navigation
      aaInput.addEventListener('keydown', (event: KeyboardEvent) => {
        const focusableEls = Array.prototype.slice.call(
          aaPanel.querySelectorAll('a')
        );
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const currentFocus = document.querySelector('#selected-query');
        let currentIndex = focusableEls.indexOf(currentFocus);
        const nextFocusableEl = focusableEls[currentIndex + 1];
        const prevFocusableEl = focusableEls[currentIndex - 1];

        if (event.key === 'ArrowDown' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex++;

          if (!nextFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            aaPanel.scroll({ top: 0, behavior: 'smooth' });

            aaInput.value = searchContext.searchTerm;
          } else {
            nextFocusableEl.classList.add('bg-stone-100');

            nextFocusableEl.setAttribute('id', 'selected-query');

            nextFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = nextFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'ArrowUp' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex--;

          if (!prevFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            if (!currentFocus) {
              lastFocusableEl.classList.add('bg-stone-100');

              lastFocusableEl.setAttribute('id', 'selected-query');
              lastFocusableEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });

              aaInput.value = lastFocusableEl.textContent.trim();
            } else {
              aaInput.value = searchContext.searchTerm;
            }
          } else {
            prevFocusableEl.classList.add('bg-stone-100');

            prevFocusableEl.setAttribute('id', 'selected-query');
            prevFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = prevFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (currentFocus?.hasAttribute('data-app-autocomplete-result')) {
            const url = currentFocus.getAttribute('href') as string;
            window.location.href = url;
          } else {
            if (currentFocus) {
              const selectedQuery = currentFocus.textContent?.trim() as string;
              aaInput.value = selectedQuery;
            }
            refine(aaInput.value);
            autocompleteSearch(aaInput.value);
            aaPanel.classList.add('hidden');
          }
        }
      });

      aaClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        aaInput.value = '';
        aaClearButton.classList.add('hidden');
      });

      geoClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        geocodeInput.value = '';
        geoClearButton.classList.add('hidden');
      });

      geocodeInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();

          const query = aaInput.value;
          const zipcode = geocodeInput.value;

          refine(query);

          if (zipcode.length > 0) {
            geocode(zipcode);
            toggleDistanceAccordion();
            insertUrlZipcode();
          } else {
            userLocation = null;
            toggleDistanceAccordion();
            removeUrlZipcode();
          }
        }
      });

      geocodeInput.addEventListener('input', () => {
        if (geocodeInput.value.length > 0) {
          geoClearButton.classList.remove('hidden');
        } else {
          geoClearButton.classList.add('hidden');
        }
      });

      // autocomplete panel section
      // aaPanel, ul
      aaPanel.setAttribute(
        'class',
        'aa-Panel search-autocomplete-panel z-50 w-full hidden'
      );

      aaForm.setAttribute('class', 'lg:flex gap-4 items-start');

      aaLoadingIndicator.setAttribute('class', 'aa-LoadingIndicator');
      aaLoadingIcon.setAttribute('class', 'aa-LoadingIcon');

      // prefix section
      // aaInput, aaLabel, aaClearButton
      aaExtendedSearchbox.setAttribute(
        'class',
        'aa-ExtendedSearchbox flex items-center relative'
      );

      const toggleQueryClearButton = aaInput.value.length > 0 ? '' : 'hidden';

      aaClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleQueryClearButton} group absolute right-3`
      );

      aaClearButton.innerHTML = `
        <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
        </svg>`;

      aaInputWrapperPrefix.setAttribute(
        'class',
        'aaInputWrapperPrefix flex-1 relative'
      );

      aaLabel.setAttribute('for', 'urmcQuery');
      aaLabel.setAttribute('class', 'mb-3 font-semibold inline-block');
      aaLabel.innerHTML = `Treatment, Condition, or Provider Name`;

      aaInput.setAttribute(
        'class',
        'border rounded w-full p-3 leading-snug border-stone-400'
      );
      aaInput.setAttribute('id', 'urmcQuery');
      aaInput.setAttribute('name', 'urmcQuery');

      aaInput.setAttribute('type', 'text');
      aaInput.setAttribute('autocomplete', 'off');

      // wrapper section
      // geocodeInput, geocodeLabel, currentLocationButton, geoclearbutton
      aaExtendedGeobox.setAttribute(
        'class',
        'aa-ExtendedGeobox flex items-center relative'
      );

      const toggleGeoClearButton =
        geocodeInput.value.length > 0 ? '' : 'hidden';

      geoClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleGeoClearButton} group absolute right-3`
      );

      geoClearButton.innerHTML = `
        <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
        </svg>`;

      geocodeInput.setAttribute(
        'class',
        'w-full rounded border border-stone-400 p-3 leading-4'
      );
      geocodeInput.setAttribute('id', 'geocodeInput');
      geocodeInput.setAttribute('type', 'text');
      geocodeInput.setAttribute('autocomplete', 'off');

      geocodeLabel.setAttribute('for', 'geocodeInput');
      geocodeLabel.setAttribute(
        'class',
        'mb-3 mt-6 font-semibold block lg:mt-0'
      );
      geocodeLabel.innerHTML = 'City or Zip Code';
      currentLocationButton.setAttribute('type', 'button');
      currentLocationButton.setAttribute('class', 'group-hover:underline');
      currentLocationButton.innerHTML = 'Use current location';

      iconContainer.setAttribute('class', 'w-3.5 h-3.5 mr-2');
      iconContainer.innerHTML = `
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            class="w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              class="fill-current"
              d="M7.34516 11.4582C7.18715 11.8451 6.77848 12.0631 6.36982 11.9841C5.96115 11.9023 5.6424 11.5454 5.6424 11.1286V6.3336H0.84741C0.455365 6.3336 0.0968307 6.03936 0.0164601 5.63069C-0.0639104 5.22203 0.156223 4.81337 0.541729 4.65535L10.1322 0.731634C10.4565 0.598682 10.8297 0.673876 11.0776 0.922071C11.3255 1.17054 11.4018 1.54378 11.2683 1.86881L7.34516 11.4582Z"
            />
          </svg>`;
      currentLocationContainer.setAttribute(
        'class',
        'flex items-center text-pacific-blue group hover:text-navy-blue cursor-pointer mt-2'
      );

      // suffix section
      // aaSubmitButton
      const spacer = document.createElement('div');
      spacer.setAttribute('class', 'search-btn-spacer');
      aaSubmitButton.setAttribute(
        'class',
        'btn btn-primary my-4 w-full max-w-none lg:my-0'
      );
      aaSubmitButton.innerHTML = 'Search';

      aaForm.onsubmit = (event) => {
        event.preventDefault();

        const query = aaInput.value;
        const zipcode = geocodeInput.value;

        refine(query);

        if (zipcode.length > 0) {
          geocode(zipcode);
          toggleDistanceAccordion();
          insertUrlZipcode();
        } else {
          userLocation = null;
          toggleDistanceAccordion();
          removeUrlZipcode();
        }
      };

      currentLocationButton.addEventListener('click', (e) => {
        e.preventDefault();
        getUserLatLng();

        geoClearButton.classList.remove('hidden');
      });

      if (
        typeof widgetParams.container === 'object' &&
        widgetParams.container !== null &&
        'appendChild' in widgetParams.container
      ) {
        widgetParams.container.appendChild(aaForm);
        widgetParams.container.appendChild(aaLoadingIndicator);

        // aaForm section
        aaForm.appendChild(aaInputWrapperPrefix);
        aaForm.appendChild(aaInputWrapper);
        aaForm.appendChild(aaInputWrapperSuffix);

        // aaExtendedSearchbox
        aaExtendedSearchbox.appendChild(aaInput);
        aaExtendedSearchbox.appendChild(aaClearButton);

        // prefix
        aaInputWrapperPrefix.appendChild(aaLabel);
        aaInputWrapperPrefix.appendChild(aaExtendedSearchbox);
        aaInputWrapperPrefix.appendChild(aaPanel);

        // aaExtendedGeobox
        aaExtendedGeobox.appendChild(geocodeInput);
        aaExtendedGeobox.appendChild(geoClearButton);

        // currentLocationContainer
        currentLocationContainer.appendChild(iconContainer);
        currentLocationContainer.appendChild(currentLocationButton);

        // wrapper
        aaInputWrapper.appendChild(geocodeLabel);
        aaInputWrapper.appendChild(aaExtendedGeobox);
        aaInputWrapper.appendChild(currentLocationContainer);

        // suffix
        aaInputWrapperSuffix.appendChild(spacer);
        aaInputWrapperSuffix.appendChild(aaSubmitButton);
      }

      refine(aaInput.value);

      // end of isFirstRender conditional
    }
  };

  const customAutocomplete = connectAutocomplete(renderAutocomplete);

  // custom sortBy
  // Create the render function
  const renderSortBy = (
    renderOptions: {
      options: any;
      currentRefinement: any;
      refine: any;
      widgetParams: any;
      canRefine: any;
    },
    isFirstRender: any
  ) => {
    const { options, currentRefinement, refine, widgetParams, canRefine } =
      renderOptions;

    if (
      typeof widgetParams.container === 'object' &&
      widgetParams.container !== null &&
      'appendChild' in widgetParams.container
    ) {
      if (isFirstRender) {
        const select = document.createElement('select');
        select.classList.add('ais-SortBy-select');

        select.addEventListener('change', () => {
          refine(select.value);

          refreshStats();
        });

        widgetParams.container.appendChild(select);
      }

      const select = widgetParams.container.querySelector('select');

      select.disabled = !canRefine;

      select.innerHTML = `
      ${options
        .map(
          (option: { value: any; label: any }) => `
          <option
          value="${option.value}"
          ${option.value === currentRefinement ? 'selected' : ''}
          >
          ${option.label}
          </option>
          `
        )
        .join('')}
          `;
    }
  };

  // Create the custom widget
  const customSortBy = connectSortBy(renderSortBy);

  // widgets and search implementation
  const renderMenu = (
    renderOptions: {
      items: any;
      refine: any;
      createURL: any;
      widgetParams: any;
    },
    isFirstRender: any
  ) => {
    const { items, refine, createURL, widgetParams } = renderOptions;

    const container = widgetParams.container;

    if (isFirstRender) {
      const ul = document.createElement('ul');

      ul.classList.add('list-none', 'space-y-4');

      if (
        typeof container === 'object' &&
        container !== null &&
        'appendChild' in container
      ) {
        container.appendChild(ul);
      }
    }

    const allGenders: string[] = ['F', 'M'];
    const currentItems: string[] = items.map(
      (item: { label: string }) => item.label
    );

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.querySelector('ul').innerHTML = allGenders
        .map((gender) => {
          const currentItem = items.filter(
            (el: { label: string }) => el.label === gender
          )[0];

          if (currentItems.includes(gender)) {
            return `
                  <li>
                    <div
                      href="${createURL(currentItem.value)}"
                      data-value="${currentItem.value}"
                      class="flex items-center"
                    >
                      <input
                        type="radio"
                        id="${currentItem.value}"
                        class="mr-2.5 ml-1 my-1"
                        name="Gender"
                        value="${currentItem.value}"
                        ${currentItem.isRefined ? 'checked="checked"' : ''}
                        >
                      <label for="${currentItem.value}">
                        ${
                          currentItem.label === 'F'
                            ? 'Female'
                            : currentItem.label === 'M'
                            ? 'Male'
                            : 'Non-Binary'
                        } (${currentItem.count})
                      </label>
                    </div>
                  </li>
          `;
          }
          return `
                <li>
                  <div
                    href="${createURL(gender)}"
                    data-value="${gender}"
                    class="flex items-center cursor-not-allowed"
                  >
                    <input
                      type="radio"
                      disabled
                      id="${gender}"
                      class="mr-2.5 ml-1 my-1 cursor-not-allowed"
                      name="Gender"
                      value="${gender}"
                      >
                    <label for="${gender}" class="text-gray-500/80 cursor-not-allowed">
                      ${
                        gender === 'F' ? 'Female' : gender === 'M' ? 'Male' : ''
                      } (0)
                    </label>
                  </div>
                </li>
          `;
        })
        .join('');

      [...container.querySelectorAll('input')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { value: any } }) => {
            refine(event.currentTarget.value);
          }
        );
      });
    }
  };

  const createDataAttributes = (refinement: any) =>
    Object.keys(refinement)
      .map((key) => `data-${key}="${refinement[key]}"`)
      .join(' ');

  const renderListItem = (item: any) =>
    `
        ${item.refinements
          .map((refinement: any) => {
            const label =
              refinement.attribute === 'IsAcceptingNewPatients' &&
              refinement.value === 'true'
                ? 'Accepting New Patients'
                : refinement.attribute === 'IsAllowOpenScheduling' &&
                  refinement.value === 'true'
                ? 'Has Online Scheduling'
                : refinement.attribute === 'Gender' && refinement.value === 'M'
                ? 'Male'
                : refinement.attribute === 'Gender' && refinement.value === 'F'
                ? 'Female'
                : refinement.label;

            const isPlainText =
              typeof label === 'string' && !/<[^>]*>/.test(label);

            if (refinement.label) {
              console.log(
                `Rendering label: ${refinement.label} with value: ${refinement.value}`
              );
            }

            if (isPlainText) {
              return `
              <li class="ais-CurrentRefinements-category filter-pill">
              ${label}
              <button
              class="text-pacific-blue ml-2"
              ${createDataAttributes(refinement)}
              >✕</button>
              </li>`;
            }
          })
          .join('')}
        `;

  const renderCurrentRefinements = (renderOptions: {
    items: any;
    refine: any;
    widgetParams: any;
  }) => {
    const { items, refine, widgetParams } = renderOptions;

    const container = widgetParams.container;

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.innerHTML = `
          <ul class="filter-list list-none">
            ${items.map(renderListItem).join('')}
          </ul>
        `;

      [...container.querySelectorAll('button')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { dataset: { [x: string]: any } } }) => {
            const item = Object.keys(event.currentTarget.dataset).reduce(
              (acc, key) => ({
                ...acc,
                [key]: event.currentTarget.dataset[key],
              }),
              {}
            );

            refine(item);
            showDivider();
          }
        );
        const disabledCheckboxes = document.querySelectorAll(
          '[disabled-checkbox]'
        );

        disabledCheckboxes.forEach((checkbox) => {
          if (element.attributes['data-label'].value === checkbox.value) {
            element.click();
          }
        });
      });
    }
  };

  const renderClearGender = (
    renderOptions: { canRefine: any; refine: any; widgetParams: any },
    isFirstRender: any
  ) => {
    const { canRefine, refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      const radioInput = document.createElement('input');
      const radioLabel = document.createElement('label');

      radioInput.setAttribute('type', 'radio');
      radioInput.setAttribute('id', 'no-preference');
      radioInput.setAttribute('class', 'mr-2.5 ml-1 my-1');
      radioInput.setAttribute('name', 'Gender');
      radioInput.setAttribute('value', 'all gender');

      radioLabel.setAttribute('for', 'no-preference');
      radioLabel.textContent = 'All Gender';

      radioInput.addEventListener('click', () => {
        refine();
        showDivider();
      });

      widgetParams.container.appendChild(radioInput);
      widgetParams.container.appendChild(radioLabel);
    }

    widgetParams.container.querySelector('input').checked = !canRefine;
  };

  const customMenu = connectMenu(renderMenu);

  const customClearGender = connectClearRefinements(renderClearGender);

  const customCurrentRefinements = connectCurrentRefinements(
    renderCurrentRefinements
  );

  // custom geo search / distance filter
  const renderGeoSearch = (
    renderOptions: {
      items: any;
      refine: any;
      widgetParams: any;
    },
    isFirstRendering: any
  ) => {
    const { refine, widgetParams } = renderOptions;

    const { container } = widgetParams;

    if (isFirstRendering) {
      const ul = document.createElement('ul');

      ul.classList.add('list-none', 'space-y-4');

      const button = document.createElement('button');
      button.textContent = 'Clear the map refinement';

      if (
        typeof container === 'object' &&
        container !== null &&
        'appendChild' in container
      ) {
        container.appendChild(ul);
      }
    }

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.querySelector('ul').innerHTML = `
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-all"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="any distance"
          data-value="all"
          ${roundedRadius === 'all' ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-all" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Any Distance</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-5"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="5 miles"
          data-value="5"
          ${roundedRadius === Math.round(1609.34 * 5) ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-5" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 5 miles</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-10"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="10 miles"
          data-value="10"
          ${roundedRadius === Math.round(16093.4) ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-10" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 10 miles</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-15"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="15 miles"
          data-value="15"
          ${roundedRadius === Math.round(1609.34 * 15) ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-15" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 15 miles</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-25"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="25 miles"
          data-value="25"
          ${roundedRadius === Math.round(1609.34 * 25) ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-25" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 25 miles</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-50"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="50 miles"
          data-value="50"
          ${roundedRadius === Math.round(16093.4 * 5) ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-50" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 50 miles</label>
      </li>
      <li ${
        userLocation === null
          ? 'class="text-gray-500/80 cursor-not-allowed" disabled'
          : ''
      }>
        <input
          type="radio"
          id="distance-100"
          class="mr-2.5 ml-1 my-1 ${
            userLocation === null ? 'text-gray-500/80 cursor-not-allowed' : ''
          }"
          name="distance"
          value="100 miles"
          data-value="100"
          ${roundedRadius === 160934 ? 'checked' : ''}
          ${userLocation === null ? 'disabled' : ''}
        >
        <label for="distance-100" ${
          userLocation === null ? 'class="cursor-not-allowed" disabled' : ''
        }>Within 100 miles</label>
      </li>
      `;

      [...container.querySelectorAll('input')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { dataset: { value: any } } }) => {
            element.checked = true;
            if (userLocation) {
              updateRadius(event.currentTarget.dataset.value);

              const pageLinkArray = Array.prototype.slice.call(
                document.querySelectorAll('.ais-Pagination-link')
              );

              const firstPageButton = pageLinkArray[0];

              firstPageButton.click();
            }
          }
        );
      });
    }
  };

  const customGeoSearch = connectGeoSearch(renderGeoSearch);

  search.addWidgets([
    configure({
      filters: 'IsActiveClinician:true',
    }),
    queryRuleCustomData({
      container: document.querySelector('[data-app-search-box]'),
      templates: {
        default: '',
      },
      transformItems(items) {
        const match = items.find((data) => Boolean(data.redirect));
        if (match && match.redirect) {
          window.location.href = match.redirect;
        }
        return [];
      },
    }),
    virtualSearchBox({}),
    customGeoSearch({
      container: document.querySelector(
        '[data-app-search-refinement="distance"]'
      ),
    }),
    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),
    customMenu({
      container: document.querySelector(
        '[data-app-search-refinement="gender"]'
      ),
      attribute: 'Gender',
      sortBy: ['name:asc'],
    }),
    customClearGender({
      container: document.querySelector(
        '[data-app-search-refinement="clear-gender"]'
      ),
      includedAttributes: ['Gender'],
    }),
    customSortBy({
      container: document.querySelector(
        '[data-app-search-refinement="sort-by"]'
      ),
      items: [
        {
          label: 'Availability',
          value: `${searchIndexName.nextAvailableAppointment}`,
        },
        {
          label: 'Distance',
          value: `${searchIndexName.distance}`,
        },
        {
          label: 'A-Z',
          value: `${searchIndexName.alphabetical}`,
        },
      ],
    }),

    clearRefinements({
      container: '[data-app-search-refinement="clear-all"]',
      cssClasses: {
        root: 'mt-4 mb-6',
        button: 'clear-refinement-btn',
      },
      templates: {
        resetLabel: 'Clear Filters',
      },
    }),

    // new patient toggle
    toggleRefinement({
      container: '[data-app-search-refinement="new-patients"]',
      attribute: 'IsAcceptingNewPatients',
      on: true,
      templates: {
        labelText() {
          return `Only show accepting new patients`;
        },
      },
      cssClasses: {
        checkbox: ['toggle accepting-new-patients'],
        label: ['toggle-label refinement-label'],
      },
    }),

    // conditions & treatments
    refinementList({
      attribute: 'RelatedConditions',
      container: '[data-app-search-refinement="related-conditions"]',
      sortBy: ['name:asc'],
      searchable: true,
      showMore: true,
      showMoreLimit: 200,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
        showMore: ['show-more-btn mt-4'],
      },
      searchablePlaceholder: 'Search the list',
      templates: {
        item({ label, count, highlighted }) {
          return `
          <label class="ais-RefinementList-label text-base">
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" value="${label}"/>
              <p>${highlighted} (${count})</p>
          </label>`;
        },
        searchableNoResults(data, { html }) {
          return html`<span>No results</span>`;
        },
      },
    }),

    // conditions & treatments
    refinementList({
      attribute: 'Languages',
      container: '[data-app-search-refinement="languages"]',
      sortBy: ['name:asc'],
      searchable: true,
      showMore: true,
      showMoreLimit: 200,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
        showMore: ['show-more-btn mt-4'],
      },
      searchablePlaceholder: 'Search the list',
      templates: {
        item({ label, count, highlighted }) {
          return `
          <label class="ais-RefinementList-label text-base">
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" value="${label}"/>
              <p>${highlighted} (${count})</p>
          </label>`;
        },
        searchableNoResults(data, { html }) {
          return html`<span>No results</span>`;
        },
      },
    }),

    refinementList({
      attribute: 'RelatedDepartments',
      container: '[data-app-search-refinement="departments"]',
      sortBy: ['name:asc'],
      searchable: true,
      showMore: true,
      showMoreLimit: 200,
      cssClasses: {
        list: ['list-none space-y-4'],
        searchableRoot: ['searchableRoot'],
        searchBox: ['refinement-searchbox'],
        searchableInput: ['refinement-input'],
        searchableSubmitIcon: [''],
        checkbox: ['refinement-checkbox'],
        showMore: ['show-more-btn mt-4'],
      },
      searchablePlaceholder: 'Search the list',
      templates: {
        item({ label, count, highlighted }) {
          return `
          <label class="ais-RefinementList-label text-base">
            <input type="checkbox" class="ais-RefinementList-checkbox refinement-checkbox" value="${label}"/>
              <p>${highlighted} (${count})</p>
          </label>`;
        },
      },
    }),

    customCurrentRefinements({
      container: document.querySelector('[data-app-search-custom-refinements]'),
    }),
    // only view open scheduling / treats children
    toggleRefinement({
      container: '[data-app-search-refinement="open-scheduling"]',
      attribute: 'IsAllowOpenScheduling',
      on: true,
      templates: {
        labelText({ isRefined }, { html }) {
          return html`<span> Online Scheduling</span>`;
        },
      },
      cssClasses: {
        checkbox: ['refinement-checkbox mr-2.5 ais-RefinementList-checkbox'],
        label: ['ais-RefinementList-label text-base flex-row refinement-label'],
      },
    }),

    hits({
      container: '[data-app-search-hits]',
      cssClasses: {
        list: 'list-none p-0 space-y-6',
        item: 'provider-result-card',
      },
      templates: {
        item(hit, { html, components }) {
          // Badge //
          const badgeSvg = html`<div class="urmfg-wedge">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="42"
              height="48"
              viewBox="0 0 42 48"
              fill="none"
            >
              <g clip-path="url(#clip0_15865_16200)">
                <path
                  d="M40.0955 0H2.58735C1.09006 0 3.09346e-05 1.09205 3.09346e-05 2.59212V46.4022C3.09346e-05 46.4022 -0.043891 49.0356 3.17828 47.5409C31.4698 34.2469 40.1354 11.3525 41.9135 2.58412C42.4778 0.132005 40.0955 0 40.0955 0Z"
                  fill="#FFD200"
                />
                <path
                  d="M5.10864 6.80981H9.7202V17.1329C9.7202 19.2188 10.8279 20.7247 13.2784 20.7247C15.7032 20.7247 16.8098 19.2188 16.8098 17.1329V6.80981H21.4219V17.2653C21.4219 21.6481 18.9188 24.7372 13.2784 24.7372C7.63871 24.7372 5.10864 21.6223 5.10864 17.2922V6.80981Z"
                  fill="#0C3066"
                />
                <path
                  d="M34.387 24.4208L31.4346 18.4536H29.1151V24.4208H24.5825V6.80981H33.3845C37.2855 6.80981 39.4998 9.39731 39.4998 12.6715C39.4998 15.7336 37.6282 17.3708 35.9942 17.952L39.5781 24.4208H34.387ZM32.7003 10.6383H29.1151V14.5981H32.7003C33.8852 14.5981 34.8876 13.8329 34.8876 12.6188C34.8876 11.4036 33.8852 10.6383 32.7003 10.6383Z"
                  fill="#0C3066"
                />
                <path
                  d="M9.63713 33.5413V29.7971L8.17383 33.5413H7.69133L6.22744 29.7971V33.5413H5.12207V28.3381H6.67134L7.93228 31.5831L9.19381 28.3381H10.7507V33.5413H9.63713Z"
                  fill="#0C3066"
                />
                <path
                  d="M11.6221 33.5414V28.3383H15.2973V29.2898H12.7286V30.4219H15.2423V31.3734H12.7286V32.5816H15.2973V33.5414H11.6221Z"
                  fill="#0C3066"
                />
                <path
                  d="M16.0049 33.5414V28.3383H18.053C19.6801 28.3383 20.8089 29.3765 20.8089 30.9433C20.8089 32.5113 19.6801 33.5414 18.053 33.5414H16.0049ZM17.1103 32.5664H18.053C19.0812 32.5664 19.6801 31.8258 19.6801 30.9433C19.6801 30.0234 19.1198 29.3133 18.053 29.3133H17.1103V32.5664Z"
                  fill="#0C3066"
                />
                <path
                  d="M21.4392 33.5414V28.3383H22.5446V33.5414H21.4392Z"
                  fill="#0C3066"
                />
                <path
                  d="M23.1682 30.9434C23.1682 29.3449 24.3753 28.2527 25.8936 28.2527C27.0774 28.2527 27.7394 28.8774 28.1044 29.5242L27.1546 29.9918C26.937 29.5711 26.4703 29.2359 25.8936 29.2359C24.9906 29.2359 24.3052 29.9613 24.3052 30.9434C24.3052 31.9266 24.9906 32.652 25.8936 32.652C26.4703 32.652 26.937 32.325 27.1546 31.8961L28.1044 32.3555C27.7312 33.0035 27.0774 33.6352 25.8936 33.6352C24.3753 33.6352 23.1682 32.543 23.1682 30.9434Z"
                  fill="#0C3066"
                />
                <path
                  d="M28.6096 33.5414V28.3383H29.715V33.5414H28.6096Z"
                  fill="#0C3066"
                />
                <path
                  d="M34.1693 33.5414L31.6942 30.0469V33.5414H30.5876V28.3383H31.7246L34.1307 31.7086V28.3383H35.2361V33.5414H34.1693Z"
                  fill="#0C3066"
                />
                <path
                  d="M36.1074 33.5414V28.3383H39.7826V29.2898H37.2128V30.4219H39.7277V31.3734H37.2128V32.5816H39.7826V33.5414H36.1074Z"
                  fill="#0C3066"
                />
              </g>
              <defs>
                <clipPath id="clip0_15865_16200">
                  <rect width="42" height="48" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>`;

          const urmfBadge = hit['IsUrmfg'] ? badgeSvg : '';

          // Accepting New Patients //
          const acceptingNewPatients = hit['IsAcceptingNewPatients']
            ? html`<div class="accepting-new-patients">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                  stroke="#038840"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                <span>Accepting New Patients</span>
              </div>`
            : '';

          // Ratings //
          const ratingsValue = hit['PGInfo']?.OverallRatingValue
            ? html`<div>
                <span
                  class="pg-star-rating"
                  style="--rating: ${hit['PGInfo']?.OverallRatingValue}"
                ></span
                ><span class="font-bold"
                  >${hit['PGInfo']?.OverallRatingValue} / 5</span
                >
                <span class="sr-only"
                  >${hit['PGInfo']?.OverallRatingValue} out of 5 stars</span
                >
              </div>`
            : '';

          const ratingsCount = hit['PGInfo']?.TotalRatingCount
            ? html`<div class="ratings-count">
                <span
                  ><a href="${hit['Url']}?clinical=true#ratings"
                    >${hit['PGInfo']?.TotalRatingCount} Ratings</a
                  ></span
                >
              </div>`
            : '';

          const ratingsSection = hit['PGInfo']?.OverallRatingValue
            ? html`<div class="ratings-section">
                ${ratingsValue} ${ratingsCount}
              </div>`
            : '';

          // Ratings & Accepting Section //
          const ratingsAndAcceptingSection =
            hit['PGInfo']?.OverallRatingValue || hit['IsAcceptingNewPatients']
              ? html`<div class="ratings-and-accepting-section">
                  ${ratingsSection} ${acceptingNewPatients}
                </div>`
              : '';

          // Phone Numbers //
          const phoneNumberWithLabels = hit['PhoneNumbers'].length
            ? hit['PhoneNumbers'].map((phone) => ({
                number: phone.Number,
                label: phone.Label,
              }))
            : [{ number: defaultPhoneNumber, label: 'Call Center' }];

          const phoneList = phoneNumberWithLabels.map((phone) => {
            return html`<div class="provider-phone-item">
              <p class="text-midnight-blue font-bold text-sm">${phone.label}</p>
              <a
                class="btn-link no-underline hover:underline"
                href="tel:${phone.number}"
                >${phone.number}</a
              >
            </div>`;
          });

          // add extensions

          const eRecordNumber = hit['ErecordProviderId'];

          const hideNextAvailable =
            hit['NextAvailableAppointment'] === '' ? 'hidden' : '';

          // Distance tag //
          const geolocArray: GeoLoc[] = hit['_geoloc'] as unknown as GeoLoc[];

          const distanceArray: number[] = geolocArray
            ? geolocArray.map((loc) => {
                return getDistance(loc.lat, loc.lng);
              })
            : [];

          function reducer(accumulator: number, currentValue: number) {
            const returns =
              accumulator >= currentValue ? currentValue : accumulator;

            return returns;
          }

          const nearestDistance =
            distanceArray.length > 0
              ? Math.round(distanceArray.reduce(reducer))
              : 'unknown';

          const showDistance =
            userLocation === null
              ? ''
              : html`<div class="distance-tab">
                  <div class="flex items-center">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      class="w-3 h-3 mr-2.5"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        class="fill-current"
                        d="M7.34516 11.4582C7.18715 11.8451 6.77848 12.0631 6.36982 11.9841C5.96115 11.9023 5.6424 11.5454 5.6424 11.1286V6.3336H0.84741C0.455365 6.3336 0.0968307 6.03936 0.0164601 5.63069C-0.0639104 5.22203 0.156223 4.81337 0.541729 4.65535L10.1322 0.731634C10.4565 0.598682 10.8297 0.673876 11.0776 0.922071C11.3255 1.17054 11.4018 1.54378 11.2683 1.86881L7.34516 11.4582Z"
                      />
                    </svg>
                    <p>${nearestDistance} miles away</p>
                  </div>
                </div>`;

          // Profile pic //
          const profPic = hit['PrimaryImage'] ?? placeholderImageURL;

          // Badge //
          const universityAffiliation =
            hit['IsUrmfg'] || hit['IsAhp'] ? 'affiliated' : 'non-affiliated';

          // Departments //
          const department =
            hit['RelatedDepartments']?.length > 0
              ? hit['RelatedDepartments'].join(', ')
              : '';

          // Schedule appointment button
          const scheduleAppointment = hit['IsAllowOpenScheduling']
            ? html`<button
                class="btn btn-primary"
                id="${eRecordNumber}"
                onclick=${() => openScheduler(eRecordNumber)}
              >
                Schedule Appointment
              </button>`
            : '';

          // Location button //
          const locationLabel =
            geolocArray?.length > 1
              ? `View all ${geolocArray.length} locations`
              : `View 1 location`;

          const locationsBtn = geolocArray?.length
            ? html`<a
                class="btn btn-secondary"
                href="${hit['Url']}?clinical=true#locations"
                >${locationLabel}</a
              >`
            : '';

          const callsToAction =
            hit['IsAllowOpenScheduling'] || geolocArray?.length
              ? html`<div class="btn-wrapper">
                  ${scheduleAppointment} ${locationsBtn}
                </div>`
              : '';

          return html`
            <div class="${universityAffiliation}">
              ${showDistance}
              <div class="sm:flex py-6">
                <div class="profile-img-container">
                  <div class="profile-img-wrapper">
                    <a
                      href="${hit['Url']}?clinical=true"
                      onMouseEnter=${() => imageMouseEnter(eRecordNumber)}
                      onMouseLeave=${() => imageMouseLeave(eRecordNumber)}
                    >
                      <img
                        class="object-cover h-full w-full"
                        src="${profPic}"
                        alt="${hit['Title']}"
                      />
                    </a>
                  </div>
                  ${urmfBadge}
                </div>
                <div class="w-full flex-1">
                  <div class="profile-title-wrapper">
                    <h3 class="text-2xl">
                      <a
                        class="btn-link text-midnight-blue no-underline hover:underline hover:text-pacific-blue"
                        href="${hit['Url']}?clinical=true"
                        id="link-${eRecordNumber}"
                      >
                        ${components.Highlight({
                          attribute: 'Title',
                          hit,
                        })}
                      </a>
                    </h3>

                    <p class="mt-2 text-midnight-blue font-bold text-base">
                      ${department}
                    </p>
                  </div>
                  ${ratingsAndAcceptingSection} ${callsToAction}
                  <div class="provider-phone-list-wrapper">${phoneList}</div>
                </div>
              </div>
            </div>
          `;
        },
        empty() {
          return '';
        },
      },
    }),

    stats({
      container: '[data-app-mobile-results-button]',
      templates: {
        text(data, {}) {
          let count = '';

          if (data.hasManyResults) {
            count += `View ${data.nbHits} Results`;
          } else if (data.hasOneResult) {
            count += `View 1 Result`;
          } else {
            count += `No Results Found`;
          }
          return count;
        },
      },
    }),

    stats({
      container: '[data-app-search-stats]',
      cssClasses: {
        text: 'stats-text',
      },
      templates: {
        text(data, { html }) {
          let count = '';

          const sortByContainer = document.querySelector('.sort-by-container');

          if (data.hasNoResults) {
            sortByContainer?.classList.add('hidden');
          } else {
            sortByContainer?.classList.remove('hidden');
          }

          if (search.status === 'error') {
            return html`
              <div>
                <p class="text-body-regular">Search is offline</p>
                <p>
                  Sorry, it appears our search tool is currently offline or
                  being worked on. The issue should be resolved soon.
                </p>
              </div>
            `;
          } else if (data.hasManyResults) {
            count += `${data.nbHits} Providers`;
          } else if (data.hasOneResult) {
            count += `1 Provider`;
          } else {
            count += `Sorry, we couldn't find any results`;
            return html`
              <p class="text-body-regular">
                <strong>${count} for: "${data.query}"</strong>
              </p>
              <p class="text-body-lg mt-10">
                Try rephrasing your search, or try browsing our
                <span> </span>
                <a href="/conditions-and-treatments">condition categories</a>
                <span> </span>
                or
                <span> </span>
                <a href="/people">primary care types</a>
                <span> </span>
                to find what you need.
              </p>
            `;
          }

          if (data.query) {
            return html` <p>
              <strong>${count}</strong> for
              <strong> "${data.query}"</strong> sorted by
              <strong> ${sortingMethod}</strong>
            </p>`;
          } else {
            return html` <p>
              <strong>${count}</strong> sorted by
              <strong> ${sortingMethod}</strong>
            </p>`;
          }
        },
      },
    }),

    pagination({
      container: '[data-app-search-pagination]',
      scrollTo: '.search-bg',
      showFirst: true,
      showLast: true,
      padding: 2,
      cssClasses: {
        list: 'justify-center remove-space list-none custom-pagination border-t',
        item: 'w-8 h-8 flex items-center justify-center overflow-hidden',
      },
    }),
  ]);

  search.start();

  // error handler to prevent uncaught errors
  search.on('error', () => {
    console.log('error', search.error);
  });

  search.on('render', () => {
    const container = document.querySelector(
      '[data-app-search-loading-indicator]'
    ) as HTMLElement;
    const hits = document.querySelector(
      '[data-app-search-hits]'
    ) as HTMLElement;

    showDivider();
    closeSchedulerListener();

    if (search.status === 'loading' || search.status === 'stalled') {
      hits.classList.add('hidden');
      container.classList.remove('hidden');
      container.innerText = 'Loading search results';
    } else {
      container.innerText = '';
    }
    if (search.status === 'idle') {
      container.classList.add('hidden');
      hits.classList.remove('hidden');
    }
  });
}
